import { useEffect, useState } from 'react';
import { hrApi } from '../../api/hr.js';
import { useAuth } from '../../contexts/AuthContext.jsx';

export default function AttendanceDashboard() {
  const { can } = useAuth();
  const isHr = can('attendance', 'list');
  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState('');
  const [items, setItems] = useState([]);
  const [today, setToday] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);

  useEffect(() => {
    if (isHr) {
      hrApi.listEmployees({ status: 'active' }).then((d) => setEmployees(d?.items || [])).catch(() => {});
    } else {
      hrApi.getMyEmployee().then((d) => setEmployeeId(d?.employee?.id || '')).catch(() => {});
    }
  }, [isHr]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      hrApi.timesheet({ employee_id: employeeId || undefined, date_from: monthStart }),
      hrApi.attendanceToday(employeeId || undefined),
    ])
      .then(([ts, t]) => {
        setItems(ts?.items || []);
        setToday(t?.record || null);
      })
      .catch((err) => setError(err.response?.data?.message || 'Failed'))
      .finally(() => setLoading(false));
  }, [employeeId, monthStart]);

  async function clockIn() {
    try { setToday((await hrApi.clockIn()).record); refresh(); } catch (e) { setError(e.response?.data?.message); }
  }
  async function clockOut() {
    try { setToday((await hrApi.clockOut()).record); refresh(); } catch (e) { setError(e.response?.data?.message); }
  }
  function refresh() {
    hrApi.timesheet({ employee_id: employeeId || undefined, date_from: monthStart }).then((d) => setItems(d?.items || []));
  }

  const totalHours = items.reduce((acc, i) => acc + Number(i.hours_worked || 0), 0).toFixed(1);

  return (
    <section>
      <header className="page-header">
        <div>
          <h1>Attendance</h1>
          <p className="muted">{items.length} entries this month · {totalHours}h total</p>
        </div>
        {!isHr && (
          <div className="actions">
            {!today?.clock_in && <button className="btn primary" onClick={clockIn}>Clock In</button>}
            {today?.clock_in && !today?.clock_out && <button className="btn primary" onClick={clockOut}>Clock Out</button>}
            {today?.clock_in && today?.clock_out && <span className="chip">Done for today</span>}
          </div>
        )}
      </header>

      {isHr && (
        <div className="card form-card">
          <label>
            View employee
            <select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
              <option value="">All employees</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.user_name}</option>)}
            </select>
          </label>
        </div>
      )}

      {error && <div className="alert error">{error}</div>}
      {loading ? <div className="muted">Loading…</div> : <Heatmap items={items} />}

      <div className="card">
        <h3>Recent entries</h3>
        <table className="data-table">
          <thead><tr><th>Date</th><th>Clock-in</th><th>Clock-out</th><th>Hours</th><th>Source</th></tr></thead>
          <tbody>
            {items.slice(0, 30).map((r) => (
              <tr key={r.id}>
                <td>{r.date}</td>
                <td>{r.clock_in?.slice(11, 16) || '—'}</td>
                <td>{r.clock_out?.slice(11, 16) || '—'}</td>
                <td>{r.hours_worked ?? '—'}</td>
                <td className="muted">{r.source}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Heatmap({ items }) {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth();
  const firstDow = new Date(year, month, 1).getDay();
  const days = new Date(year, month + 1, 0).getDate();

  const map = new Map(items.map((i) => [i.date, i.hours_worked || 0]));
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push({ key: `pad-${i}`, pad: true });
  for (let d = 1; d <= days; d++) {
    const date = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
    const h = map.get(date) ?? null;
    let level = 0;
    if (h != null) level = h >= 8 ? 4 : h >= 6 ? 3 : h >= 4 ? 2 : h > 0 ? 1 : 0;
    cells.push({ key: date, day: d, h, level });
  }

  return (
    <div className="card">
      <h3>{today.toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
      <div className="cal-grid">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="cal-head muted small">{d}</div>
        ))}
        {cells.map((c) => (c.pad ? <div key={c.key} /> : (
          <div key={c.key} className={`cal-cell lvl-${c.level}`} title={`${c.key}: ${c.h ?? 'no record'}h`}>
            <span className="day">{c.day}</span>
            <span className="muted small">{c.h != null ? `${c.h}h` : ''}</span>
          </div>
        )))}
      </div>
    </div>
  );
}
